import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Event } from '@/types';
import { Loader2 } from 'lucide-react';

interface NewEventModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (eventData: Omit<Event, 'id' | 'created_at' | 'workspace_id'>) => Promise<void>;
  editEvent?: Event | null; // Add the event to edit
  isEditing?: boolean; // Flag to indicate if we're editing
}

export const NewEventModal: React.FC<NewEventModalProps> = ({ 
  open, 
  onOpenChange, 
  onSubmit, 
  editEvent = null,
  isEditing = false
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    fecha: '',
    contacto1_nombre: '',
    contacto1_tel: '',
    contacto1_email: '',
    contacto2_nombre: '',
    contacto2_tel: '',
    contacto2_email: '',
    status: 'DRAFT'
  });

  // Load event data when editing
  useEffect(() => {
    if (editEvent) {
      setFormData({
        nombre: editEvent.nombre,
        fecha: editEvent.fecha.toISOString().split('T')[0],
        contacto1_nombre: editEvent.contacto1_nombre,
        contacto1_tel: editEvent.contacto1_tel,
        contacto1_email: editEvent.contacto1_email,
        contacto2_nombre: editEvent.contacto2_nombre || '',
        contacto2_tel: editEvent.contacto2_tel || '',
        contacto2_email: editEvent.contacto2_email || '',
        status: editEvent.status
      });
    }
  }, [editEvent]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.nombre || !formData.fecha || !formData.contacto1_nombre || !formData.contacto1_tel || !formData.contacto1_email) {
      alert('Por favor, completa todos los campos requeridos');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const eventData: Omit<Event, 'id' | 'created_at' | 'workspace_id'> = {
        nombre: formData.nombre,
        fecha: new Date(formData.fecha),
        contacto1_nombre: formData.contacto1_nombre,
        contacto1_tel: formData.contacto1_tel,
        contacto1_email: formData.contacto1_email,
        contacto2_nombre: formData.contacto2_nombre || undefined,
        contacto2_tel: formData.contacto2_tel || undefined,
        contacto2_email: formData.contacto2_email || undefined,
        status: formData.status as 'DRAFT' | 'ACTIVE' | 'ARCHIVED'
      };

      await onSubmit(eventData);
      
      // Reset form after successful submission if not editing
      if (!isEditing) {
        setFormData({
          nombre: '',
          fecha: '',
          contacto1_nombre: '',
          contacto1_tel: '',
          contacto1_email: '',
          contacto2_nombre: '',
          contacto2_tel: '',
          contacto2_email: '',
          status: 'DRAFT'
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Editar Evento' : 'Crear Nuevo Evento'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="nombre">Nombre del evento *</Label>
            <Input 
              id="nombre" 
              placeholder="Ej: Boda de Ana y Carlos"
              value={formData.nombre}
              onChange={(e) => handleInputChange('nombre', e.target.value)}
              disabled={isSubmitting}
              required
            />
          </div>
          
          <div>
            <Label htmlFor="fecha">Fecha del evento *</Label>
            <Input 
              id="fecha" 
              type="date"
              value={formData.fecha}
              onChange={(e) => handleInputChange('fecha', e.target.value)}
              disabled={isSubmitting}
              required
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="contacto1_nombre">Contacto principal *</Label>
              <Input 
                id="contacto1_nombre" 
                placeholder="Nombre"
                value={formData.contacto1_nombre}
                onChange={(e) => handleInputChange('contacto1_nombre', e.target.value)}
                disabled={isSubmitting}
                required
              />
            </div>
            <div>
              <Label htmlFor="contacto1_tel">Teléfono *</Label>
              <Input 
                id="contacto1_tel" 
                placeholder="+52 55 1234 5678"
                value={formData.contacto1_tel}
                onChange={(e) => handleInputChange('contacto1_tel', e.target.value)}
                disabled={isSubmitting}
                required
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="contacto1_email">Email del contacto *</Label>
            <Input 
              id="contacto1_email" 
              type="email" 
              placeholder="contacto@email.com"
              value={formData.contacto1_email}
              onChange={(e) => handleInputChange('contacto1_email', e.target.value)}
              disabled={isSubmitting}
              required
            />
          </div>

          <div className="border-t pt-4">
            <h4 className="font-medium mb-3 text-gray-700">Contacto secundario (opcional)</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contacto2_nombre">Nombre</Label>
                <Input 
                  id="contacto2_nombre" 
                  placeholder="Nombre"
                  value={formData.contacto2_nombre}
                  onChange={(e) => handleInputChange('contacto2_nombre', e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <Label htmlFor="contacto2_tel">Teléfono</Label>
                <Input 
                  id="contacto2_tel" 
                  placeholder="+52 55 1234 5678"
                  value={formData.contacto2_tel}
                  onChange={(e) => handleInputChange('contacto2_tel', e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
            </div>
            <div className="mt-3">
              <Label htmlFor="contacto2_email">Email</Label>
              <Input 
                id="contacto2_email" 
                type="email" 
                placeholder="contacto2@email.com"
                value={formData.contacto2_email}
                onChange={(e) => handleInputChange('contacto2_email', e.target.value)}
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button 
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {isEditing ? 'Actualizando...' : 'Creando...'}
                </>
              ) : (
                isEditing ? 'Guardar Cambios' : 'Crear Evento'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
